'use client';

/**
 * Modal — blocking overlay that demands a decision (theme T5d §5).
 *
 * - Backdrop click closes non-destructive modals only.
 * - Esc always closes.
 * - Focus is trapped + restored on close (simple implementation — good
 *   enough for our scale; if accessibility audits need more, swap to
 *   @radix-ui/react-dialog wholesale).
 * - `<ConfirmModal>` is a specialisation for destructive actions with
 *   default-focus on Cancel (safety) and a red primary button.
 */
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Button } from './Button';
import { cn } from './cn';
import { IconButton } from './IconButton';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Open modals, innermost last. A quick-add popup opened from inside another
 * modal (the client conversation's "+ Create client") must own Escape and
 * Tab on its own: without the stack, Escape closed both and the outer trap
 * pulled focus out of the inner dialog.
 */
const openModals: object[] = [];

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[680px]',
  xl: 'max-w-[960px]',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  size?: ModalSize;
  /** Close on backdrop click.  False for destructive / form-with-changes modals. */
  dismissOnBackdrop?: boolean;
  children: ReactNode;
  /** Footer slot — typically <Cancel> <Confirm>. */
  footer?: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  dismissOnBackdrop = true,
  children,
  footer,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const tokenRef = useRef<object | null>(null);
  // The latest `onClose`, read at key time. The open effect below is keyed
  // on `open` alone: keyed on `onClose` too, a caller passing a fresh
  // function each render (an inline arrow, a form's own close) re-ran it on
  // every keystroke — focus jumped back to the first field.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Keyed on `open` alone, so a new `onClose` identity on re-render does not
  // move this modal above one opened after it.
  useEffect(() => {
    if (!open) return;
    const token = {};
    tokenRef.current = token;
    openModals.push(token);
    return () => {
      const at = openModals.indexOf(token);
      if (at >= 0) openModals.splice(at, 1);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const isTop = () => openModals[openModals.length - 1] === tokenRef.current;
    restoreRef.current = document.activeElement as HTMLElement | null;
    // Focus first focusable inside, fallback to dialog itself.
    setTimeout(() => {
      const first = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      (first ?? dialogRef.current)?.focus();
    }, 0);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent): void => {
      if (!isTop()) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
      if (e.key === 'Tab' && dialogRef.current) {
        trapFocus(e, dialogRef.current);
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      restoreRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  // The portal lands on document.body, outside the (app) layout's
  // data-surface="workspace" subtree — so without this attribute a modal
  // reads the *unpinned* neutral scale, which under data-theme="dark" is the
  // dark one: near-white headings and pale input text on the modal's white
  // panel. The modal belongs to the shell it opened from; say so.
  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto px-4 pt-[10vh]"
      data-surface="workspace"
      role="presentation"
      onClick={(e) => {
        if (dismissOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        aria-hidden
        className="bg-slate-900/60 fixed inset-0 z-overlay"
        onClick={() => dismissOnBackdrop && onClose()}
      />
      <div
        ref={dialogRef}
        aria-labelledby="modal-title"
        aria-modal="true"
        className={cn(
          'relative z-modal w-full rounded-xl bg-white p-8 shadow-4 focus:outline-none',
          SIZE_CLASSES[size],
        )}
        role="dialog"
        tabIndex={-1}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-slate-900" id="modal-title">
              {title}
            </h2>
            {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}
          </div>
          <IconButton aria-label="Close" icon={X} size="sm" variant="tertiary" onClick={onClose} />
        </div>

        <div className="mt-6">{children}</div>

        {footer ? <div className="mt-8 flex items-center justify-end gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// ConfirmModal — specialised destructive confirmation
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive red primary by default.  Set false for a normal primary. */
  destructive?: boolean;
  isLoading?: boolean;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = true,
  isLoading = false,
}: ConfirmModalProps) {
  return (
    <Modal
      description={description}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button disabled={isLoading} variant="secondary" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            isLoading={isLoading}
            variant={destructive ? 'destructive' : 'primary'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
      open={open}
      size="sm"
      title={title}
      onClose={onClose}
    >
      {null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function trapFocus(event: KeyboardEvent, container: HTMLElement): void {
  const focusable = container.querySelectorAll<HTMLElement>(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}
